import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

import PreviewProcessingOverlay from '../common/PreviewProcessingOverlay';
import { LoadingOverlay } from '../common/Spinner';
import { INTERACTIVE_IFRAME_FIT } from './interactiveCanvasFit';

import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import type { Renderer } from '@src/renderer/Renderer';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';

export type ClipPreviewPlaybackState = {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  isSeeking: boolean;
};

type Props = {
  /**
   * CSS `aspect-ratio` value for the poster (e.g. '1080 / 1920'), from
   * `useRenderedAspectRatio` so the poster matches the frame the iframe fills.
   */
  aspectRatio?: string;
  autoPlay?: boolean;
  poster?: string;
  /**
   * PLAT-11445: the composition is audio-first (renditions pending, no
   * session file on the canvas). The iframe would paint black — hide it
   * behind the shared processing veil while audio and transcript stay live.
   */
  processingVeil?: boolean;
  /** Shared renderer props from useClipPreviewRenderer. */
  sharedRendererProps: {
    /** Renderer mode; 'interactive' draws the frame inset, see INTERACTIVE_IFRAME_FIT. */
    mode: 'player' | 'interactive';
    rendererContainerRef: React.RefObject<HTMLDivElement>;
    rendererRef: React.MutableRefObject<Renderer | null>;
    renderer: Renderer | null;
    isReady: boolean;
    isLoading: boolean;
    editorStore: ClipPreviewEditorStore | null;
    videoSourceStore: VideoSourceStore | null;
    play: () => void;
    pause: () => void;
    setTime: (time: number) => void;
  };
};

/**
 * Pure canvas container for the Creatomate iframe. Overlays would intercept
 * pointer events needed for interactive drag/resize — transport controls
 * live in `ClipPreviewControlBar` outside this component, and playback
 * state is owned by `usePlaybackState` at the parent level.
 */
const CreatomateClipPreview = observer(
  ({ sharedRendererProps, poster, aspectRatio, processingVeil }: Props) => {
    const { rendererContainerRef, isReady, isLoading, mode } =
      sharedRendererProps;
    const veiled = !!processingVeil && isReady && !isLoading;

    // Show the poster only during the very first load (before the iframe is
    // ready). On subsequent source swaps (aspect-ratio toggle, variant
    // switch) the iframe fades through ClipPreview's opacity guard — showing
    // the poster here would flash the previous variant's thumbnail (with its
    // OLD aspect ratio) inside the resized canvas before the prop catches up.
    const showPoster = !!poster && !isReady;

    return (
      <PreviewContainer
        data-testid="creatomate-clip-preview"
        data-ready={isReady || undefined}
      >
        <RendererContainer
          ref={rendererContainerRef}
          $interactive={mode === 'interactive'}
          style={{
            opacity: isReady && !isLoading && !veiled ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
        />
        {veiled && (
          <PreviewProcessingOverlay data-testid="clip-preview-processing-veil" />
        )}
        {poster && (
          <PosterImage
            src={poster}
            alt=""
            $visible={showPoster}
            aspectRatio={aspectRatio}
          />
        )}
        {(!isReady || isLoading) && <LoadingOverlay message="Loading video…" />}
      </PreviewContainer>
    );
  },
);

export default CreatomateClipPreview;

const PreviewContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
`;

const PosterImage = styled.img<{
  $visible: boolean;
  aspectRatio?: string;
}>`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 100%;
  object-fit: cover;
  z-index: 1;
  opacity: ${props => (props.$visible ? 1 : 0)};
  aspect-ratio: ${props => props.aspectRatio};
  transition: opacity 0.3s ease-in-out;
  pointer-events: none;
`;

// The composition fills the container exactly: 1:1 in 'player' mode, and
// INTERACTIVE_IFRAME_FIT in 'interactive' mode. A caller box that doesn't
// match the composition's ratio gets letterbox bars, never cropped content.
const RendererContainer = styled.div<{ $interactive?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;

    ${props => props.$interactive && INTERACTIVE_IFRAME_FIT}
  }
`;
